import React from 'react';

import type { GroupRecord, StreamRecord } from '@vlabs/api/artemy/model';
import { DeleteIcon } from '@vlabs/icons';
import { GeoLocation, GeoLocationMarker } from '@vlabs/uikit';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';
import { useDynamicCoordinates } from '@vlabs/uikit/components/geo-location/useDynamicCoordinates';

import {
  buildLocationString,
  getAnalyticsTypeLabel,
  hasUnsyncedParams,
  parseAnalyticsLabel,
  SynchronizeStatus,
  toStreamAnalyticsRecordItem,
  toStreamLocationRecord,
} from '@vlabs/monokit/entities/sources';
import { StreamStatusWithActions } from '@vlabs/monokit/entities/streams';
import { confirmDeleteStreams, DeleteStreamsIconButton } from '@vlabs/monokit/features/delete-stream';
import { DuplicateStreamIconButton } from '@vlabs/monokit/features/duplicate-stream';
import { GoToEditStreamIconButton } from '@vlabs/monokit/features/go-to-edit-stream';
import { StreamLiveButton } from '@vlabs/monokit/features/stream-live';
import { StreamLogsLink } from '@vlabs/monokit/features/stream-logs';
import { CopyableText } from '@vlabs/monokit/shared/ui/copyable-text';
import {
  AnalyticStatus,
} from '@vlabs/monokit/shared/ui/stream-statuses';
import type { BuildTableViewCellProps, TableCtx, TBuildTableViewCell } from '@vlabs/monokit/shared/ui/table-view';
import {
  buildTableViewCell,
  getTestIdFromTableCtx,
} from '@vlabs/monokit/shared/ui/table-view';

import st from './shared.module.sass';

export { renderHeader, renderCell } from '@vlabs/monokit/shared/ui/table-view';

type TSharedCtx<TValue = unknown> = TableCtx<StreamRecord, TValue>;

const getStreamRowTestId = (stream: StreamRecord): string | undefined => stream.stream_id;

function createStreamCell(
  props: BuildTableViewCellProps,
): <TValue>(ctx: TSharedCtx<TValue> | undefined) => TBuildTableViewCell {
  return (ctx) => buildTableViewCell({
    ...props,
    // min-width: 0 разрешает flex-сжатие — иначе длинный контент (uuid) растягивает ячейку
    style: { minWidth: 0, ...props.style },
    ...getTestIdFromTableCtx(ctx, getStreamRowTestId),
  });
}

// Ширины колонок — по макетам «Источники» и «Группа источников» (идентичны)
export const numberCell = createStreamCell({ style: { flex: '0 0 40px' } });
export const liveCell = createStreamCell({ style: { flex: '0 0 180px' } });
// 184px по макету; колонка может расти при переполнении (оправданный горизонтальный скролл)
export const nameCell = createStreamCell({ style: { flex: '1 1 184px' } });
export const statusCell = createStreamCell({ style: { flex: '0 0 200px' } });
export const analyticsStatusesCell = createStreamCell({ style: { flex: '0 0 248px' } });
export const locationCell = createStreamCell({ style: { flex: '0 0 212px' } });
// ID не растёт, но может сжиматься; полный текст в тултипе CopyableText + копирование
export const idCell = createStreamCell({ style: { flex: '0 1 150px' } });
export const logsCell = createStreamCell({ style: { flex: '0 0 72px' } });
export const actionCell = createStreamCell({
  style: { flex: '0 0 40px', justifyContent: 'center', padding: '0 0' },
});
export const dividerCell = (): TBuildTableViewCell => buildTableViewCell({
  style: { flex: '0 0 16px', padding: 0 },
});

export function StreamNumberCellContent({
  index,
}: { index: number }): string {
  return String(index + 1);
}

export function StreamNameCellContent({
  stream,
  group,
}: { stream: StreamRecord; group: GroupRecord }): React.JSX.Element {
  const synchronized = !hasUnsyncedParams(group, stream);

  return (
    <span className={st.NameCell}>
      {stream.name ?? stream.stream_id}
      <SynchronizeStatus synchronized={synchronized} />
    </span>
  );
}

export function StreamLiveCellContent({ stream }: { stream: StreamRecord }): React.JSX.Element | null {
  if (!stream.stream_id) return null;

  return (
    <StreamLiveButton
      size="small"
      streamId={stream.stream_id}
    />
  );
}

export function StreamStatusWithActionsCellContent({
  stream,
}: { stream: StreamRecord }): React.JSX.Element | null {
  if (!stream.stream_id) return null;

  // Статуса нет в ответе списка (enriched=0) — самодостаточная сущность
  // запрашивает его сама (targets=['status']), как в шапках страниц (GUI-3863)
  return <StreamStatusWithActions streamId={stream.stream_id} />;
}

export function StreamAnalyticsStatusesCellContent({
  stream,
}: { stream: StreamRecord }): React.JSX.Element | null {
  const items = (stream.analytics ?? [])
    .map((analytics) => toStreamAnalyticsRecordItem(analytics));

  if (items.length === 0) return null;

  return (
    <div className={st.AnalyticsStatuses}>
      {items.map((item) => {
        const labelParts = parseAnalyticsLabel(item.label);
        const title = getAnalyticsTypeLabel(labelParts?.type ?? item.type ?? '');

        return (
          <AnalyticStatus
            key={item.label ?? item.type}
            name={title}
            number={labelParts ? String(labelParts.number) : undefined}
            status={stream.status ?? 6}
          />
        );
      })}
    </div>
  );
}

// Есть координаты — мини-карта с пином; есть только адрес — текст; ничего нет — заглушка GeoLocation
export function StreamLocationCellContent({ stream }: { stream: StreamRecord }): React.JSX.Element | string {
  const location = toStreamLocationRecord(stream.location);

  const latitude = location?.geo_position?.latitude;
  const longitude = location?.geo_position?.longitude;
  const hasCoordinates = latitude !== undefined && longitude !== undefined;

  const position = useDynamicCoordinates({
    latitude: hasCoordinates ? String(latitude) : '',
    longitude: hasCoordinates ? String(longitude) : '',
  });

  if (hasCoordinates) {
    return (
      <div className={st.MapCell}>
        <GeoLocation
          center={position}
          mapProps={{ mouseEvents: false, touchEvents: false }}
          toggleFullScreenMode={false}
        >
          <GeoLocationMarker anchor={position} />
        </GeoLocation>
      </div>
    );
  }

  const address = buildLocationString(location);

  if (address) {
    return (
      <span className={st.LocationText}>{address}</span>
    );
  }

  return (
    <div className={st.MapCell}>
      <GeoLocation
        mapProps={{ mouseEvents: false, touchEvents: false }}
        toggleFullScreenMode={false}
      />
    </div>
  );
}

export function StreamIdCellContent({ stream }: { stream: StreamRecord }): React.JSX.Element | null {
  if (!stream.stream_id) return null;
  return <CopyableText value={stream.stream_id} />;
}

export function StreamLogsCellContent({ stream }: { stream: StreamRecord }): React.JSX.Element | null {
  if (!stream.stream_id) return null;

  return (
    <StreamLogsLink
      size="small"
      streamId={stream.stream_id}
    />
  );
}

export function StreamEditCellContent({
  stream,
}: { stream: StreamRecord }): React.JSX.Element | null {
  if (!stream.stream_id) return null;
  return <GoToEditStreamIconButton streamId={stream.stream_id} />;
}

export function StreamDuplicateCellContent({
  stream,
  groupId,
}: { stream: StreamRecord; groupId: string }): React.JSX.Element | null {
  if (!stream.stream_id) return null;
  return (
    <DuplicateStreamIconButton
      groupId={groupId}
      streamId={stream.stream_id}
    />
  );
}

export function StreamDeleteCellContent({
  stream,
  onEnqueueDelete = undefined,
}: {
  stream: StreamRecord;
  onEnqueueDelete?: (streamIds: string[]) => void;
}): React.JSX.Element | null {
  if (!stream.stream_id) return null;
  const streamId = stream.stream_id;

  if (onEnqueueDelete) {
    return (
      <IconButton
        icon={<DeleteIcon />}
        kind="negative"
        onClick={() => confirmDeleteStreams({
          onConfirm: () => onEnqueueDelete([streamId]),
        })}
        variant="flat"
      />
    );
  }

  return (
    <DeleteStreamsIconButton
      kind="negative"
      streamIds={[streamId]}
    />
  );
}
